import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase';
import LoadingSpinner from '../components/LoadingSpinner';

// Constants
const ERROR_MESSAGES = {
  NO_TOKEN: "No authentication token found in the URL.",
  INVALID_TOKEN: "The authentication token is invalid or has expired.",
  SIGN_IN_FAILED: "Failed to authenticate with the provided token."
};

// This component handles authentication tokens passed from the marketing website
const AuthHandler = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  useEffect(() => {
    const processToken = async () => {
      try {
        console.log("[AuthHandler] Starting authentication process...");
        
        // Check if user is already logged in
        if (currentUser) {
          console.log("[AuthHandler] User already logged in:", { 
            uid: currentUser.uid,
            email: currentUser.email,
            emailVerified: currentUser.emailVerified
          });
          navigate("/dashboard");
          return;
        }

        // Get the token from URL query parameters
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get("token");

        if (!token) {
          console.error("[AuthHandler] No token found in URL parameters");
          setError(ERROR_MESSAGES.NO_TOKEN);
          setLoading(false);
          return;
        }

        console.log("[AuthHandler] Custom token retrieved from URL", {
          tokenLength: token.length,
          tokenStart: token.substring(0, 10) + "...",
          urlParams: Array.from(queryParams.keys())
        });

        // Attempt to sign in with the custom token
        console.log("[AuthHandler] Attempting to sign in with custom token...");
        try {
          const userCredential = await signInWithCustomToken(auth, token);
          console.log("[AuthHandler] Sign in successful!", {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            emailVerified: userCredential.user.emailVerified,
            isAnonymous: userCredential.user.isAnonymous,
            providerData: userCredential.user.providerData.map(p => ({
              providerId: p.providerId,
              email: p.email
            })),
            metadata: {
              creationTime: userCredential.user.metadata.creationTime,
              lastSignInTime: userCredential.user.metadata.lastSignInTime
            }
          });
          
          // Redirect to dashboard after successful login
          navigate("/dashboard");
        } catch (signInError) {
          console.error("[AuthHandler] Sign in with custom token failed:", signInError);
          
          // Log detailed error information
          console.error("[AuthHandler] Error details:", {
            code: signInError.code || "unknown",
            message: signInError.message || "unknown",
            additionalData: signInError.customData || {}
          });
          
          if (signInError.code === "auth/invalid-custom-token") {
            setError(ERROR_MESSAGES.INVALID_TOKEN);
          } else {
            setError(`${ERROR_MESSAGES.SIGN_IN_FAILED} (${signInError.code || "unknown error"})`);
          }
        }
      } catch (error) {
        console.error("[AuthHandler] Unexpected error in authentication process:", error);
        setError("An unexpected error occurred. Please try logging in again.");
      } finally {
        setLoading(false);
      }
    };

    processToken();
  }, [currentUser, location, navigate]);

  const handleReturnToLogin = () => {
    console.log("[AuthHandler] User redirected to login page after error");
    navigate("/login");
  };

  return (
    <div className="auth-handler-container">
      {loading ? (
        <div className="loading-state">
          <LoadingSpinner />
          <p>Authenticating...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <h2>Authentication Error</h2>
          <p>{error}</p>
          <button onClick={handleReturnToLogin} className="btn btn-primary">
            Return to Login
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default AuthHandler; 